/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DirectionalHint, mergeStyleSets } from '@fluentui/react';
import { ITooltipProps } from '@fluentui/react/lib/Tooltip';
import * as React from 'react';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getRBACTooltipStyles } from 'src/core/utils/azureRbacStyles';
import { globalMessages } from 'src/core/utils/globalMessages';

export type RBACTooltipProps = ITooltipProps & {
    /**
     * The component on which tht tooltip should appear when hovered on
     */
    componentSurroundedByToolTip: JSX.Element;

    /**
     * The user role to display in the default luis tool tip message
     * In case another message is passed in the content props, the default
     * message is not shown.
     */
    displayedUserRole: string;
};

/**
 * Renders a tool tip that surround components which are disabled
 * by RBAC, for example command bar buttons
 *
 * It differs from the common tool tip is that
 * @param props The input properties for the tooltip.
 */

export const RBACTooltip = (props: RBACTooltipProps) => {
    const { componentSurroundedByToolTip, displayedUserRole, hidden, content, styles, ...toolTipProps } = props;

    const { localize } = useLocalization();

    return (
        <LuisTooltip
            hidden={hidden}
            tooltipProps={toolTipProps}
            directionalHint={DirectionalHint.leftTopEdge}
            styles={!hidden ? mergeStyleSets(getRBACTooltipStyles(), styles) : styles}
            content={!content ? localize(globalMessages.cannotPerformAction, { displayedUserRole }) : content}>
            {componentSurroundedByToolTip}
        </LuisTooltip>
    );
};
